import React, { useState, useEffect } from 'react';
import { X, User, Phone, Star, Check } from 'lucide-react';
import { CoordinatorData } from '../../types';

interface CoordinatorAssignDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  slotId: string;
  startTime: number;
  endTime: number;
  availableCoordinators: CoordinatorData[];
  assignedCoordinators: Record<string, string[]>;
  onAssignCoordinator: (coordinatorId: string, slotId: string, level: number) => void;
  onClose: () => void;
}

const CoordinatorAssignDialog: React.FC<CoordinatorAssignDialogProps> = ({
  open,
  setOpen,
  slotId,
  startTime,
  endTime,
  availableCoordinators,
  assignedCoordinators,
  onAssignCoordinator,
  onClose
}) => {
  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const handleAssignCoordinator = (coordinatorId: string, level: number) => {
    onAssignCoordinator(coordinatorId, slotId, level);
  };

  const isAssigned = (coordinatorId: string) => {
    return assignedCoordinators[slotId]?.includes(coordinatorId) || false;
  };

  const getLevelButtonColor = (level: number) => {
    switch (level) {
      case 3: return 'bg-red-100 text-red-700 border-red-200 hover:bg-red-200';
      case 2: return 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200';
      case 1: return 'bg-green-100 text-green-700 border-green-200 hover:bg-green-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200';
    }
  };

  const getLevelLabel = (level: number) => {
    switch (level) {
      case 3: return 'Head';
      case 2: return 'Main';
      case 1: return 'Trainee';
      default: return 'Unknown';
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Select Coordinator</h3>
          <button
            onClick={handleClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close dialog"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 max-h-96 overflow-y-auto">
          {availableCoordinators.length === 0 ? (
            <div className="text-center py-8">
              <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">No Coordinator Available</h4>
              <p className="text-gray-600">No coordinators are available for the selected time range.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {availableCoordinators.map((coordinator) => {
                const assigned = isAssigned(coordinator.coordinatorId);
                const displayName = coordinator.coordinatorName;
                const phoneNumber = coordinator.coordinatorPhoneNumber;
                const profilePic = coordinator.coordinatorPic;
                
                return (
                  <div 
                    key={coordinator.coordinatorId} 
                    className={`p-4 border rounded-lg transition-colors ${
                      assigned 
                        ? 'bg-primary-50 border-primary-200' 
                        : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                          {profilePic ? (
                            <img 
                              src={`https://res.cloudinary.com/travelmanage/image/upload/v1657687406/${profilePic}`}
                              alt={displayName}
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            <User className="h-5 w-5 text-gray-500" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 truncate max-w-[200px]">
                            {displayName.length > 18 ? `${displayName.slice(0, 18)}...` : displayName}
                          </div>
                          {phoneNumber && (
                            <div className="text-sm text-gray-500 flex items-center space-x-1">
                              <Phone className="h-3 w-3" />
                              <span>{phoneNumber}</span>
                            </div>
                          )}
                          {coordinator.ratingCount > 0 && (
                            <div className="text-sm text-gray-500 flex items-center space-x-1">
                              <Star className="h-3 w-3" />
                              <span>{coordinator.rating} ({coordinator.ratingCount})</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        {!coordinator.available ? (
                          <div className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm font-medium">
                            {coordinator.note || 'Already Assigned'}
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => handleAssignCoordinator(coordinator.coordinatorId, 3)}
                              disabled={assigned || !coordinator.available}
                              className={`px-3 py-1 rounded text-sm font-medium border transition-colors ${
                                assigned || !coordinator.available
                                  ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                  : getLevelButtonColor(3)
                              }`}
                            >
                              Head {coordinator.headCount ? `(${coordinator.headCount})` : ''}
                            </button>
                            <button
                              onClick={() => handleAssignCoordinator(coordinator.coordinatorId, 2)}
                              disabled={assigned || !coordinator.available}
                              className={`px-3 py-1 rounded text-sm font-medium border transition-colors ${
                                assigned || !coordinator.available
                                  ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                  : getLevelButtonColor(2)
                              }`}
                            >
                              Main {coordinator.mainCount ? `(${coordinator.mainCount})` : ''}
                            </button>
                            <button
                              onClick={() => handleAssignCoordinator(coordinator.coordinatorId, 1)}
                              disabled={assigned || !coordinator.available}
                              className={`px-3 py-1 rounded text-sm font-medium border transition-colors ${
                                assigned || !coordinator.available
                                  ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                  : getLevelButtonColor(1)
                              }`}
                            >
                              Trainee {coordinator.traineeCount ? `(${coordinator.traineeCount})` : ''}
                            </button>
                          </>
                        )}
                        
                        {assigned && (
                          <div className="flex items-center text-green-600">
                            <Check className="h-5 w-5" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoordinatorAssignDialog;
